import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	MenuList,
	MenuListDivider,
	ListItem,
	Input,
	Checkbox
} from '@clickguard/design-system'
import { AdAccountDTO } from 'api-models'
import { useAccountStore, useAssetStore, useUiStore } from 'store'
import { useStores } from 'hooks'
import { useShallow } from 'zustand/react/shallow'
import { PlatformIcon } from 'components/platform-icon'
import { ModalTypes } from 'components/basic-blocks/g-modal/types'
import { ConnectAdAccountComponent } from 'components/setup-v2/connect-ad-account-component'
import Dataflow02 from '@untitled-ui/icons-react/build/esm/Dataflow02'
import Plus from '@untitled-ui/icons-react/build/esm/Plus'
import Settings04 from '@untitled-ui/icons-react/build/esm/Settings04'
import { adAccountLabel } from 'utils/ad-account-label'
import { BreadcrumbTrigger } from './breadcrumb-trigger'

type Platform = 'GADS' | 'FADS' | 'BADS'

const PLATFORM_LABELS: Record<Platform, string> = {
	GADS: 'Google Ads',
	FADS: 'Meta Ads',
	BADS: 'Microsoft Ads'
}

interface AdAccountsSelectorProps {
	adAccounts: AdAccountDTO[]
	selectedAdAccounts: AdAccountDTO[]
	onApply: (accounts: AdAccountDTO[]) => void
}

export const AdAccountsSelector = ({
	adAccounts,
	selectedAdAccounts,
	onApply
}: AdAccountsSelectorProps) => {
	const { accountStore, userStore } = useStores()
	const { user } = userStore

	const { asset, refreshAdAccounts } = useAssetStore(
		useShallow((s) => ({
			asset: s.asset,
			refreshAdAccounts: s.refreshAdAccounts
		}))
	)
	const { account } = useAccountStore(
		useShallow((s) => ({ account: s.account }))
	)
	const setModalContent = useUiStore((s) => s.setModalContent)
	const setModalState = useUiStore((s) => s.setModalState)
	const navigate = useNavigate()

	const [isOpen, setIsOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	const [draftSelection, setDraftSelection] = useState<AdAccountDTO[]>([])
	const containerRef = useRef<HTMLDivElement>(null)

	const userAccess = accountStore.userAccessLevel(user?.id || '')
	const isManagerOrOwner = userAccess === 'OWNER' || userAccess === 'WRITE'

	const platformCounts = useMemo(() => {
		const counts: Record<Platform, number> = { GADS: 0, FADS: 0, BADS: 0 }
		adAccounts.forEach((acc) => {
			counts[acc.platform] = (counts[acc.platform] || 0) + 1
		})
		return counts
	}, [adAccounts])

	const allPlatforms: Platform[] = ['GADS', 'FADS', 'BADS']

	const visibleAccounts = useMemo(() => {
		if (!searchQuery) return adAccounts
		return adAccounts.filter((acc) =>
			adAccountLabel(acc).toLowerCase().includes(searchQuery.toLowerCase())
		)
	}, [adAccounts, searchQuery])

	const platformSelectionState = useMemo(() => {
		const state: Record<Platform, 'all' | 'some' | 'none'> = {
			GADS: 'none',
			FADS: 'none',
			BADS: 'none'
		}
		allPlatforms.forEach((platform) => {
			const platformAccounts = adAccounts.filter(
				(acc) => acc.platform === platform
			)
			if (platformAccounts.length === 0) {
				state[platform] = 'none'
				return
			}
			const selectedOfPlatform = platformAccounts.filter((acc) =>
				draftSelection.some((s) => s.id === acc.id)
			).length
			if (selectedOfPlatform === platformAccounts.length) {
				state[platform] = 'all'
			} else if (selectedOfPlatform > 0) {
				state[platform] = 'some'
			} else {
				state[platform] = 'none'
			}
		})
		return state
	}, [adAccounts, draftSelection])

	const totalCount = adAccounts.length
	const selectedCount = draftSelection.length

	const allVisibleSelected =
		visibleAccounts.length > 0 &&
		visibleAccounts.every((acc) => draftSelection.some((s) => s.id === acc.id))
	const someVisibleSelected =
		!allVisibleSelected &&
		visibleAccounts.some((acc) => draftSelection.some((s) => s.id === acc.id))

	const handleOpen = () => {
		setDraftSelection(
			selectedAdAccounts.length === 0
				? [...adAccounts]
				: [...selectedAdAccounts]
		)
		setSearchQuery('')
		setIsOpen(true)
	}

	useEffect(() => {
		if (!isOpen) return
		const handleClickOutside = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				onApply(draftSelection)
				setIsOpen(false)
				setSearchQuery('')
			}
		}
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setIsOpen(false)
				setSearchQuery('')
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleEscape)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [isOpen, draftSelection, onApply])

	const toggleAccount = (account: AdAccountDTO) => {
		setDraftSelection((prev) => {
			const exists = prev.some((a) => a.id === account.id)
			return exists
				? prev.filter((a) => a.id !== account.id)
				: [...prev, account]
		})
	}

	const handleSelectAll = () => {
		if (allVisibleSelected) {
			const visibleIds = new Set(visibleAccounts.map((a) => a.id))
			setDraftSelection((prev) => prev.filter((a) => !visibleIds.has(a.id)))
		} else {
			setDraftSelection((prev) => {
				const existing = new Set(prev.map((a) => a.id))
				const toAdd = visibleAccounts.filter((a) => !existing.has(a.id))
				return [...prev, ...toAdd]
			})
		}
	}

	const togglePlatform = (platform: Platform) => {
		const platformAccounts = adAccounts.filter(
			(acc) => acc.platform === platform
		)
		if (platformAccounts.length === 0) return
		const allOfPlatformSelected = platformAccounts.every((acc) =>
			draftSelection.some((s) => s.id === acc.id)
		)
		if (allOfPlatformSelected) {
			const platformIds = new Set(platformAccounts.map((a) => a.id))
			setDraftSelection((prev) => prev.filter((a) => !platformIds.has(a.id)))
		} else {
			setDraftSelection((prev) => {
				const existing = new Set(prev.map((a) => a.id))
				const toAdd = platformAccounts.filter((a) => !existing.has(a.id))
				return [...prev, ...toAdd]
			})
		}
	}

	const handleManageConnectionsClick = () => {
		setIsOpen(false)
		if (!account?.id || !asset?.id) return
		navigate(`/settings/workspace/${account.id}/asset/${asset.id}/connections`)
	}

	const handleConnectAdAccount = () => {
		setIsOpen(false)
		if (!account || !asset || !user) return
		setModalContent({
			type: ModalTypes.INFO,
			actionsEnabled: false,
			showIcon: false,
			rootClassName: 'bg-transparent',
			body: (
				<ConnectAdAccountComponent
					account={account}
					isOnboarding={false}
					user={user}
					asset={asset}
					onSuccess={async () => {
						await refreshAdAccounts()
						setModalState(false)
					}}
				/>
			)
		})
	}

	const isAllSelected =
		selectedAdAccounts.length === 0 || selectedAdAccounts.length === totalCount
	const label = isAllSelected
		? 'All ad accounts'
		: `${selectedAdAccounts.length} ad accounts`

	return (
		<div className="relative flex min-w-0" ref={containerRef}>
			<BreadcrumbTrigger
				icon={
					<Dataflow02 className="size-4 text-[var(--text-secondary,#535255)]" />
				}
				label={label}
				onClick={() => {
					if (totalCount === 0) return
					isOpen ? setIsOpen(false) : handleOpen()
				}}
				isOpen={isOpen}
			/>

			{isOpen && (
				<div className="absolute top-full left-0 mt-1 z-50">
					<MenuList className="min-w-[430px] w-max !p-[10px]" role="listbox">
						{/* Header: "Ad accounts · X of Y selected" */}
						<div className="flex items-center gap-[4px] px-[4px] leading-[16px]">
							<span className="font-['Host_Grotesk',sans-serif] text-[12px] leading-[16px] text-[var(--text-secondary,#535255)] whitespace-nowrap">
								Ad accounts
							</span>
							<span className="font-['Host_Grotesk',sans-serif] text-[12px] leading-[16px] text-[var(--text-tertiary,#9f9ea3)]">
								•
							</span>
							<span className="font-['Host_Grotesk',sans-serif] text-[12px] leading-[16px] text-[var(--text-tertiary,#9f9ea3)] whitespace-nowrap">
								{selectedCount} of {totalCount} selected
							</span>
						</div>

						{/* Platform bulk-select tiles */}
						<div className="flex gap-[10px] mt-[10px]">
							{allPlatforms.map((platform) => {
								const count = platformCounts[platform]
								const hasAccounts = count > 0
								const selectionState = platformSelectionState[platform]
								const isSelected = selectionState === 'all'
								const isPartial = selectionState === 'some'

								return (
									<button
										key={platform}
										type="button"
										onClick={() => hasAccounts && togglePlatform(platform)}
										className={`
											relative box-border min-w-[130px] shrink-0 h-[70px] flex flex-col items-center justify-center gap-[4px] px-[12px] py-[8px] rounded-[12px] border border-solid transition-colors
											${
												!hasAccounts
											? 'border-t-checkbox bg-[var(--bg-secondary,#f9f9f9)] opacity-40 cursor-not-allowed'
													: isSelected || isPartial
													? 'border-[var(--border-brand-medium,#7169ff)] bg-[var(--lavender-fill-soft,rgba(238,237,254,0.5))]'
											: 'border-t-checkbox bg-[rgb(var(--cg-surface))] hover:bg-[var(--bg-hover,#f9f9f9)] cursor-pointer'
											}
										`}
									>
										{isSelected && (
											<div className="absolute top-[9px] right-[9px] w-[9.333px] h-[9.333px] rounded-[2.333px] bg-[#554bfd] border-[0.583px] border-solid border-[#554bfd] flex items-center justify-center overflow-hidden">
												<svg
													width="7"
													height="7"
													viewBox="0 0 12 12"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M10 3L4.5 8.5L2 6"
														stroke="white"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											</div>
										)}
										{isPartial && (
											<div className="absolute top-[9px] right-[9px] w-[9.333px] h-[9.333px] rounded-[2.333px] bg-[#554bfd] border-[0.583px] border-solid border-[#554bfd] flex items-center justify-center overflow-hidden">
												<svg
													width="7"
													height="2"
													viewBox="0 0 12 2"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M2 1H10"
														stroke="white"
														strokeWidth="2"
														strokeLinecap="round"
													/>
												</svg>
											</div>
										)}
										<PlatformIcon
											platform={platform}
											className="w-[22px] h-[22px]"
										/>
										<span className="font-['Host_Grotesk',sans-serif] text-[12px] leading-[16px] whitespace-nowrap text-[var(--text-link,#554bfd)]">
											{hasAccounts
												? PLATFORM_LABELS[platform]
												: 'No accounts connected'}
										</span>
									</button>
								)
							})}
						</div>

						{/* Search */}
						<div className="mt-[10px]">
							<Input
								size="sm"
								state="default"
								placeholder="Search ad accounts..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
						</div>

						{/* Select All — full-bleed (like the list below) so the checkbox
						    lines up with the account rows. */}
						<div className="mt-[4px] -mx-[10px]">
							<ListItem
								label="Select all"
								leadingControl={
									<div
										className="flex items-center"
										onClick={(e) => e.stopPropagation()}
									>
										<Checkbox
											checked={allVisibleSelected}
											indeterminate={someVisibleSelected}
											onChange={handleSelectAll}
											size="sm"
										/>
									</div>
								}
								onClick={handleSelectAll}
							/>
						</div>

						<MenuListDivider className="-mx-[10px]" />

						{/* Account list — full-bleed horizontally (rows + scrollbar reach
						    the popover edges); no bottom bleed since the Connect footer
						    sits below it. */}
						<div className="max-h-[200px] overflow-y-auto -mx-[10px]">
							{visibleAccounts.length > 0 ? (
								visibleAccounts.map((acc) => {
									const isChecked = draftSelection.some((a) => a.id === acc.id)
									return (
										<ListItem
											key={acc.id}
											label={adAccountLabel(acc)}
											leadingControl={
												<div
													className="flex items-center"
													onClick={(e) => e.stopPropagation()}
												>
													<Checkbox
														checked={isChecked}
														onChange={() => toggleAccount(acc)}
														size="sm"
													/>
												</div>
											}
											leadingIcon={
												<PlatformIcon
													platform={acc.platform}
													className="w-4 h-4"
												/>
											}
											onClick={() => toggleAccount(acc)}
										/>
									)
								})
							) : (
								<div className="px-[8px] py-[8px]">
									<span className="font-['Host_Grotesk',sans-serif] text-[14px] leading-[18px] text-[var(--text-tertiary,#9f9ea3)]">
										No ad accounts found
									</span>
								</div>
							)}
						</div>
						<>
							<MenuListDivider />
							<ListItem
								label="Manage connections"
								leadingIcon={<Settings04 className="size-5" />}
								onClick={handleManageConnectionsClick}
							/>
						</>

						{/* Connect CTA */}
						{isManagerOrOwner && (
							<>
								<MenuListDivider className="-mx-[10px]" />
								<ListItem
									className="-mx-[10px]"
									label="Connect new ad account"
									leadingIcon={<Plus className="size-5" />}
									onClick={handleConnectAdAccount}
								/>
							</>
						)}
					</MenuList>
				</div>
			)}
		</div>
	)
}
